/**
 * Escalonador de grupos de horário da roteirização (ida/volta).
 *
 * Os grupos compartilham o pool de reuso de veículos: um grupo só enxerga o que os anteriores
 * já registraram. Rodar todos em paralelo mudaria o resultado, então cada grupo declara de
 * quais anteriores depende (`buildScheduleDependencies`) e o escalonador (`runScheduledGroups`)
 * só dispara o cálculo quando as dependências terminaram. O "aplicar" de cada grupo (estado
 * da tela) sai sempre em ordem cronológica, via `commitGate`.
 *
 * CommonJS de propósito: o mesmo arquivo é usado pelo bundle da tela, pelo worker do
 * scheduler (pipeline da roteirização) e por tests/*.mjs.
 */

function toMinutes(hhmm) {
    if (hhmm == null) return null;
    const m = String(hhmm).trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

/**
 * `i` (anterior) pode ceder algum veículo a `j`? Qualquer sentido (o reuso não alterna mais
 * ida/volta). Só limites de tempo, sempre pelo lado conservador (na dúvida, dependência):
 *  - fim de `i`: a ida chega no CD em `time`; a volta termina depois da saída (`time`);
 *  - início de `j`: a ida sai ~maxTrip antes da chegada; a volta sai do CD em `time`;
 *  - ida → volta: as duas no CD (mesmo local), então vale o tempo mínimo de transferência;
 *    nos demais pares os locais podem diferir e o deslocamento é desconhecido (limite 0).
 */
function canYield(i, j, { maxTripSec, carenciaSec }) {
    const ti = toMinutes(i.time);
    const tj = toMinutes(j.time);
    if (ti == null || tj == null) return true;
    let startJ = tj * 60;
    if (j.type === 'ida') {
        const tripSec = Number(maxTripSec);
        if (!Number.isFinite(tripSec)) return true;
        startJ -= tripSec;
    }
    const minTransferSec = i.type === 'ida' && j.type === 'volta' ? carenciaSec : 0;
    return startJ >= ti * 60 + minTransferSec;
}

/**
 * @param {Array<{type:'ida'|'volta', time:string}>} queue fila em ordem cronológica
 * @returns {number[][]} deps[j] = índices (< j) dos quais j depende
 */
function buildScheduleDependencies(queue, { maxTripSec, carenciaSec = 0 } = {}) {
    const opts = { maxTripSec, carenciaSec: Math.max(0, Number(carenciaSec) || 0) };
    const n = queue.length;
    const yields = Array.from({ length: n }, () => new Array(n).fill(false));
    for (let i = 0; i < n; i += 1) {
        for (let j = i + 1; j < n; j += 1) yields[i][j] = canYield(queue[i], queue[j], opts);
    }
    const deps = [];
    for (let j = 0; j < n; j += 1) {
        const set = new Set();
        for (let i = 0; i < j; i += 1) {
            if (yields[i][j]) {
                set.add(i);
                continue;
            }
            // Grupos que disputam os veículos da mesma origem: se um anterior `k` pode ceder
            // a `i` e a `j`, a ordem entre eles altera quem fica com o veículo.
            for (let k = 0; k < i; k += 1) {
                if (yields[k][i] && yields[k][j]) { set.add(i); break; }
            }
        }
        deps.push([...set].sort((a, b) => a - b));
    }
    return deps;
}

/**
 * Executa `run(index)` respeitando `deps` e `concurrency`.
 *
 * Deadlock: um grupo só pode aplicar (commitGate.enter) depois que TODOS os anteriores
 * terminaram, então um grupo adiantado pode ficar parado esperando um anterior ainda não
 * iniciado. Por isso, um grupo só "fura a fila" se sobrar pelo menos 1 vaga reservada ao
 * menor grupo ainda não iniciado.
 *
 * `run(index, { commitGate })` deve chamar `await commitGate.enter()` antes de mexer no
 * estado compartilhado. Erros de um grupo não cancelam os outros: viram `{ error }` no
 * resultado daquele índice.
 *
 * @returns {Promise<Array<{value?:any, error?:any}>>}
 */
function runScheduledGroups(count, deps, run, { concurrency = 1 } = {}) {
    const limit = Math.max(1, Math.floor(Number(concurrency)) || 1);
    const results = new Array(count);
    const started = new Array(count).fill(false);
    const done = new Array(count).fill(false);
    const gateWaiters = [];
    let running = 0;
    let finished = 0;

    const allDoneBefore = (idx) => {
        for (let k = 0; k < idx; k += 1) if (!done[k]) return false;
        return true;
    };
    const flushGate = () => {
        for (let w = gateWaiters.length - 1; w >= 0; w -= 1) {
            if (allDoneBefore(gateWaiters[w].idx)) {
                const [waiter] = gateWaiters.splice(w, 1);
                waiter.resolve();
            }
        }
    };

    return new Promise((resolve) => {
        if (!count) { resolve(results); return; }

        const pump = () => {
            for (let idx = 0; idx < count; idx += 1) {
                if (started[idx]) continue;
                const ready = (deps[idx] || []).every((d) => done[d]);
                const lowestUnstarted = started.indexOf(false) === idx;
                const slots = lowestUnstarted ? limit : limit - 1;
                if (!ready) continue;
                if (running >= slots) {
                    if (lowestUnstarted) break; // sem vaga nem para o menor: espera terminar alguém
                    continue;
                }
                started[idx] = true;
                running += 1;
                const commitGate = {
                    enter: () => (allDoneBefore(idx)
                        ? Promise.resolve()
                        : new Promise((res) => gateWaiters.push({ idx, resolve: res }))),
                };
                Promise.resolve()
                    .then(() => run(idx, { commitGate }))
                    .then((value) => { results[idx] = { value }; })
                    .catch((error) => { results[idx] = { error }; })
                    .then(() => {
                        done[idx] = true;
                        running -= 1;
                        finished += 1;
                        flushGate();
                        if (finished === count) resolve(results);
                        else pump();
                    });
            }
        };
        pump();
    });
}

module.exports = { buildScheduleDependencies, runScheduledGroups, canYield, toMinutes };
